import { useEffect, useState } from "react";
import Base from "../../Base/views/Base";
import Box from '@mui/material/Box';
import {
    Button,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
    Divider,
    Stack
} from "@mui/material";
import Popup from "../../components/Popup";
import Swal from "sweetalert2";
import axiosInstance from "../../../axiosInstance";
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

const GuestLandingPage = () => {
    const hotelID = localStorage.getItem("billing_app_hotel");
    const [data, setData] = useState([]);
    const [open, setOpen] = useState(false);

    const initialValues = {
        name: "",
        email: "",
        phone: "",
        address: "",
        aadhar_number: "",
        gst_number: "",
        vehicle_number: ""
    };

    const [inputData, setInputData] = useState(initialValues);
    const [errors, setErrors] = useState(initialValues);

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        axiosInstance(`/api/guest/?hotel=${hotelID}`, { method: "GET" })
            .then(res => setData(res.data))
            .catch(err => console.log(err));
    };

    const validate = (fieldValues = inputData) => {
        let temp = { ...errors };
        if ("name" in fieldValues)
            temp.name = fieldValues.name ? "" : "Name is required.";
        setErrors({ ...temp });
        return Object.values(temp).every(x => x === "");
    };

    const handleChange = (e) => {
        setInputData({ ...inputData, [e.target.name]: e.target.value });
    };

    const handleClose = () => {
        setOpen(false);
        setInputData(initialValues);
        setErrors(initialValues);
    };

    const handleSubmit = () => {
        let api = inputData?.id ? `/api/guest/${inputData.id}/` : `/api/guest/`;
        let method = inputData?.id ? "PUT" : "POST";
        if (validate()) {
            axiosInstance(api, {
                method: method,
                data: {
                    ...inputData,
                    hotel: hotelID
                }
            }).then(res => {
                getData();
                setOpen(false);
                setInputData(initialValues);
                Swal.fire({
                    icon: 'success',
                    title: inputData?.id ? 'Guest updated!' : 'Guest added!',
                    timer: 1500
                });
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: err.response?.data?.detail || 'Failed to save guest',
                });
            });
        }
    };

    const handleClick = (res) => {
        setOpen(true);
        setInputData(res);
    };

    const handleDelete = (e, id) => {
        e?.stopPropagation();
        Swal.fire({
            title: "Are you sure?",
            text: "This guest will be deleted.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            icon: 'warning',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance(`/api/guest/${id}/`, { method: "DELETE" })
                    .then(res => {
                        getData();
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted!',
                            timer: 1500
                        });
                    })
                    .catch(err => console.log(err));
            }
        });
    };

    return (
        <Base>
            {/* Popup Form */}
            <Popup
                open={open}
                handleClose={handleClose}
                title={inputData?.id ? "Edit Guest" : "Add New Guest"}
                handleSubmit={handleSubmit}
                maxWidth="sm"
            >
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Full Name"
                        fullWidth
                        required
                        name="name"
                        onChange={handleChange}
                        value={inputData?.name || ""}
                        size="small"
                        sx={{ mb: 2 }}
                        {...(errors.name && { error: true, helperText: errors.name })}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="Email"
                            fullWidth
                            name="email"
                            onChange={handleChange}
                            value={inputData?.email || ""}
                            size="small"
                        />
                        <TextField
                            label="Phone"
                            fullWidth
                            // required
                            name="phone"
                            onChange={handleChange}
                            value={inputData?.phone || ""}
                            size="small"
                            {...(errors.phone && { error: true, helperText: errors.phone })}
                        />
                    </Box>

                    <TextField
                        label="Address"
                        fullWidth
                        name="address"
                        onChange={handleChange}
                        value={inputData?.address || ""}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="Aadhaar Number"
                            fullWidth
                            name="aadhar_number"
                            onChange={handleChange}
                            value={inputData?.aadhar_number || ""}
                            size="small"
                        />
                        <TextField
                            label="GST Number"
                            fullWidth
                            name="gst_number"
                            onChange={handleChange}
                            value={inputData?.gst_number || ""}
                            size="small"
                        />
                    </Box>

                    <TextField
                        label="Vehicle Number"
                        fullWidth
                        name="vehicle_number"
                        onChange={handleChange}
                        value={inputData?.vehicle_number || ""}
                        size="small"
                        sx={{ mb: 2 }}
                    />
                </Box>
            </Popup>

            {/* Heading removed - now in header */}

            {/* Guests List */}
            <Grid container spacing={2}>
                {data.map((res) => (
                    <Grid item xs={12} sm={6} md={4} key={res.id}>
                        <Card 
                            sx={{ 
                                p: 1.5,
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                    boxShadow: '0 4px 12px rgba(139, 68, 255, 0.15)',
                                    transform: "translateY(-3px)",
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography fontWeight="600">{res.name}</Typography>
                                <Box>
                                    <IconButton size="small" onClick={() => handleClick(res)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={(e) => handleDelete(e, res.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Phone:</Typography>
                                    <Typography>{res.phone || "-"}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Email:</Typography>
                                    <Typography>{res.email || "-"}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Address:</Typography>
                                    <Typography>{res.address ?? '-'}</Typography>
                                </Box>
                                {
                                    res.aadhar_number && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Aadhaar:</Typography>
                                            <Typography>{res.aadhar_number ?? '-'}</Typography>
                                        </Box>
                                    )
                                }
                                {
                                    res.gst_number && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">GST:</Typography>
                                            <Typography>{res.gst_number ?? '-'}</Typography>
                                        </Box>
                                    )
                                }
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Vehicle:</Typography>
                                    <Typography>{res.vehicle_number ?? '-'}</Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Empty State */}
            {data.length === 0 && (
                <Box sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Guests Found
                    </Typography>
                    {/* <Button
                        variant="contained"
                        onClick={() => setOpen(true)}
                    >
                        Add Guest
                    </Button> */}
                </Box>
            )}
        </Base>
    );
};

export default GuestLandingPage;